import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import ChevronLeftIcon from "@redotech/redo-icons/chevron-left_filled.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right_filled.svg";
import LayoutLeftSvg from "@redotech/redo-icons/layout-left.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useRef } from "react";
import { useStatusFilter } from "../filters/filters-context";
import {
  STATUS_OPTIONS,
  type StatusValue,
} from "../filters/support-tickets-filter-toolbar";
import { useSupportTicketsTableContext } from "../table/support-tickets-table-context";
import {
  SupportTicketCard,
  SupportTicketCardEmpty,
  SupportTicketCardSkeleton,
} from "./support-ticket-card";

export const SupportTicketCardList = memo(function SupportTicketCardList({
  onCollapse,
}: {
  onCollapse: () => void;
}) {
  const {
    data: conversations,
    isLoading,
    currentPage,
    pageSize,
    totalCount,
    hasPreviousPage,
    hasNextPage,
    goToNextPage,
    goToPreviousPage,
    selectedSupportTicketId,
    openSupportTicket,
  } = useSupportTicketsTableContext();

  const { status, setStatus } = useStatusFilter();

  const hasScrolledRef = useRef(false);
  const selectedCardRef = useCallback((node: HTMLDivElement | null) => {
    if (node && !hasScrolledRef.current) {
      hasScrolledRef.current = true;
      node.scrollIntoView({ block: "center" });
    }
  }, []);

  const handleStatusChange = useCallback(
    ({ item }: { item: RedoListItem<StatusValue> }) => {
      setStatus(item.value === "all" ? null : item.value);
    },
    [setStatus],
  );

  const selectedStatusItem = useMemo(() => {
    if (!status) {
      return STATUS_OPTIONS.find((o) => o.id === "all");
    }
    return STATUS_OPTIONS.find((o) => o.id === status);
  }, [status]);

  const handleCardClick = useCallback(
    (conversation: SupportTicketListItemResponse) => {
      openSupportTicket(conversation.id);
    },
    [openSupportTicket],
  );

  const startIndex = currentPage * pageSize + 1;
  const endIndex = Math.min(startIndex + conversations.length - 1, totalCount);

  return (
    <Flex bgColor="primary" dir="column" gap="none" height="full">
      <Flex
        align="center"
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        gap="sm"
        pl="md"
        pr="md"
        shrink={0}
        style={{ height: 48 }}
      >
        <Flex>
          <RedoSingleSelectDropdownInput
            options={STATUS_OPTIONS}
            optionSelected={handleStatusChange}
            selectedItem={selectedStatusItem}
            size="xs"
          />
        </Flex>
        <Flex align="center" gap="xs">
          <RedoButton
            disabled={!hasPreviousPage}
            hierarchy="tertiary"
            IconLeading={ChevronLeftIcon}
            onClick={goToPreviousPage}
            size="xs"
          />
          <Text fontSize="xs" textColor="secondary">
            {totalCount > 0
              ? `${startIndex}-${endIndex} of ${totalCount}`
              : "0"}
          </Text>
          <RedoButton
            disabled={!hasNextPage}
            hierarchy="tertiary"
            IconLeading={ChevronRightIcon}
            onClick={goToNextPage}
            size="xs"
          />
        </Flex>
        <Flex align="center" flex={1} justify="flex-end">
          <RedoButton
            hierarchy="tertiary"
            IconLeading={LayoutLeftSvg}
            onClick={onCollapse}
            size="sm"
          />
        </Flex>
      </Flex>

      <Flex
        dir="column"
        flex={1}
        gap="none"
        overflowY="auto"
        style={{ minHeight: 0 }}
      >
        {isLoading ? (
          <SupportTicketCardSkeleton count={5} />
        ) : conversations.length === 0 ? (
          <SupportTicketCardEmpty />
        ) : (
          conversations.map((conversation) => {
            const selected = conversation.id === selectedSupportTicketId;
            return (
              <div
                key={conversation.id}
                ref={selected ? selectedCardRef : undefined}
              >
                <SupportTicketCard
                  conversation={conversation}
                  isActive={selected}
                  onClick={() => handleCardClick(conversation)}
                />
              </div>
            );
          })
        )}
      </Flex>
    </Flex>
  );
});
